import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { TestCaseService } from '../../../services/test-case.service';
import { TestCase, TestCaseRevision } from '../../../models/test-case.model';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';

// Muss synchron zur serverseitigen Revisionierung bleiben: nur diese Felder
// werden in TestCaseRevision versioniert, daher zählen nur sie als Änderung.
const COMPARED_FIELDS = ['title', 'description', 'testGoal', 'preconditions', 'assigneeUserId'] as const;

@Component({
  selector: 'app-version-diff-panel',
  imports: [TranslatePipe, MatProgressSpinner, MatIcon, SectionTitleComponent],
  templateUrl: './version-diff-panel.component.html',
  styleUrl: './version-diff-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionDiffPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  readonly current = input<TestCase | null>(null);

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly loaded = signal(false);
  readonly previous = signal<TestCaseRevision | null>(null);

  readonly changedFields = computed(() => {
    const current = this.current();
    const previous = this.previous();
    if (!current || !previous) return [];
    return COMPARED_FIELDS.filter((field) => normalize(current[field]) !== normalize(previous[field]));
  });

  readonly changeCount = computed(() => this.changedFields().length);

  ngOnInit(): void {
    const id = this.testCaseId();
    if (!id) return;
    this.loadPrevious(id);
  }

  private loadPrevious(id: string): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.testCaseService
      .getPreviousRevision(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (revision) => {
          this.previous.set(revision);
          this.loaded.set(true);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }
}

function normalize(value: string | number | null | undefined): string {
  return value == null ? '' : String(value);
}
